import { cliente } from "../http/cliente";
import type {
  Canal,
  CanalIn,
  CanalPatch,
  Regra,
  RegraIn,
  RegraPatch,
  TesteCanal,
} from "./tipos";

/*
 * Fetchers do módulo de Canais de Alerta (FE-6a — Tela 6).
 *
 * Camada fina sobre o `cliente` HTTP (CP-C): injeta Bearer, trata refresh/401/503
 * e normaliza erro em `ErroHttp`. Estes fetchers NÃO tratam erro — propagam a
 * rejeição do `cliente` para o TanStack Query (hooks em `./hooks`) decidir.
 *
 * `baseURL` do cliente já é `/api`, então os paths aqui são relativos a ela. PATCH
 * usa `cliente.patch` (campos parciais). O `config` (write-only) vai SÓ no corpo do
 * POST/PATCH (HTTPS) — nunca em query string nem path.
 */

/** `GET /api/canais` — canais no escopo `configurar_alertas` (sem `config`). */
export async function listarCanais(): Promise<Canal[]> {
  const resposta = await cliente.get<Canal[]>("/canais");
  return resposta.data;
}

/** `POST /api/canais` (201). `corpo.config` é write-only (nunca volta). */
export async function criarCanal(corpo: CanalIn): Promise<Canal> {
  const resposta = await cliente.post<Canal>("/canais", corpo);
  return resposta.data;
}

/** `GET /api/canais/{id}` (sem `config`). */
export async function obterCanal(id: string): Promise<Canal> {
  const resposta = await cliente.get<Canal>(`/canais/${id}`);
  return resposta.data;
}

/**
 * `PATCH /api/canais/{id}`. `corpo.config` AUSENTE = mantém a credencial atual;
 * presente = re-cifra. `tipo` não entra no PATCH (imutável).
 */
export async function atualizarCanal(
  id: string,
  corpo: CanalPatch,
): Promise<Canal> {
  const resposta = await cliente.patch<Canal>(`/canais/${id}`, corpo);
  return resposta.data;
}

/**
 * `POST /api/canais/{id}/testar` — dispara um envio de teste pelo canal.
 *
 * Ação dedicada `testar_canal` (gate SEPARADO de `configurar_alertas`). Falha de
 * entrega NÃO é erro HTTP: vem 200 com `ok: false`. Sem corpo de request.
 */
export async function testarCanal(id: string): Promise<TesteCanal> {
  const resposta = await cliente.post<TesteCanal>(`/canais/${id}/testar`);
  return resposta.data;
}

/* ─────────────────────────────── Regras (FE-6b) ─────────────────────────────── */

/** `GET /api/regras` — regras no escopo `configurar_alertas` do usuário. */
export async function listarRegras(): Promise<Regra[]> {
  const resposta = await cliente.get<Regra[]>("/regras");
  return resposta.data;
}

/** `POST /api/regras` (201). `corpo.limite` é a string decimal canônica. */
export async function criarRegra(corpo: RegraIn): Promise<Regra> {
  const resposta = await cliente.post<Regra>("/regras", corpo);
  return resposta.data;
}

/** `GET /api/regras/{id}`. */
export async function obterRegra(id: string): Promise<Regra> {
  const resposta = await cliente.get<Regra>(`/regras/${id}`);
  return resposta.data;
}

/**
 * `PATCH /api/regras/{id}` (campos parciais). `empresa_id` NÃO entra no PATCH
 * (imutável no backend — `RegraPatch` não o expõe).
 */
export async function atualizarRegra(
  id: string,
  corpo: RegraPatch,
): Promise<Regra> {
  const resposta = await cliente.patch<Regra>(`/regras/${id}`, corpo);
  return resposta.data;
}
